<template>
    <div
        v-if="shouldShowWebPhone"
        class="webphone-container"
        :class="{
            'webphone-minimized': isMinimized,
            'webphone-maximized': !isMinimized,
            'webphone-dragging': isDragging,
            shouldShowContactOverlay:
                shouldShowContactOverlay && !isContactOverlayCollapsed && !isMinimized && foundContacts.length > 0,
        }"
        :style="minimizedStyle"
    >
        <!-- Minimized Mode -->
        <div
            v-if="isMinimized"
            class="webphone-minimized-button"
            @mousedown="startDrag"
            :class="{
                'status-connected': sipStatus.isRegistered,
                'status-calling': callState.status === 'calling',
                'status-ringing': callState.status === 'ringing',
                'incoming-call': callState.status === 'incoming',
            }"
        >
            <div class="status-indicator"></div>
            <i class="material-icons">
                {{ getMinimizedIcon() }}
            </i>
            <div v-if="callState.status === 'incoming'" class="incoming-call-info">
                <div class="caller-name">{{ callState.callerName || callState.callerNumber }}</div>
            </div>
        </div>

        <!-- Maximized Mode -->
        <div v-else class="webphone-maximized-wrapper" :style="maximizedWrapperStyle">
            <!-- WebPhone Content -->
            <div class="webphone-maximized-content">
                <!-- Header -->
                <div class="webphone-header" @mousedown="startDragMaximized">
                    <div class="header-left">
                        <div class="sip-status" :class="{ connected: sipStatus.isRegistered }">
                            <div class="status-dot"></div>
                            <span>{{ sipStatus.isRegistered ? "Bağlı" : "Bağlantı Kesildi" }}</span>
                        </div>
                    </div>
                    <div class="header-center">
                        <h3>{{ $_.get(telnyxChannel, "name", "Telnyx Phone") }}</h3>
                        <div class="phone-number" v-if="$_.get(telnyxChannel, 'payload.callerPhoneNumber', null)">
                            {{ telnyxChannel.payload.callerPhoneNumber }}
                        </div>
                    </div>
                    <div class="header-right">
                        <button class="minimize-btn" @click="toggleMode">
                            <i class="material-icons">remove</i>
                        </button>
                    </div>
                </div>

                <!-- Content Tabs -->
                <div class="webphone-tabs">
                    <button class="tab-btn" :class="{ active: activeTab === 'dialer' }" @click="activeTab = 'dialer'">
                        <i class="material-icons">dialpad</i>
                        <span>Tuş Takımı</span>
                    </button>
                    <button class="tab-btn" :class="{ active: activeTab === 'history' }" @click="activeTab = 'history'">
                        <i class="material-icons">history</i>
                        <span>Geçmiş</span>
                    </button>
                </div>

                <!-- Tab Content -->
                <div class="webphone-content">
                    <!-- Dialer Tab -->
                    <WebPhoneDialer
                        v-if="activeTab === 'dialer'"
                        ref="dialerComponent"
                        :call-state="callState"
                        :dial-number="dialNumber"
                        :sip-status="sipStatus"
                        :extensions="extensions"
                        :mapping="{}"
                        @dial="handleDial"
                        @call="handleCall"
                        @hangup="handleHangup"
                        @answer="handleAnswer"
                        @reject="handleReject"
                        @mute="handleMute"
                        @hold="handleHold"
                        @transfer="handleTransfer"
                        @dtmf="handleDTMF"
                        @number-change="dialNumber = $event"
                        @play-dtmf-tone="playDTMFTone($event)"
                    />

                    <!-- Call History Tab -->
                    <WebPhoneCallHistory
                        v-if="activeTab === 'history'"
                        :call-history="callHistory"
                        @call="handleCallFromHistory"
                        @clear-history="clearCallHistory"
                    />
                </div>
            </div>
        </div>

        <!-- Incoming Call Notification (when minimized) -->
        <div v-if="isMinimized && callState.status === 'incoming'" class="incoming-call-notification">
            <div class="caller-info">
                <div class="caller-avatar">
                    <i class="material-icons">person</i>
                </div>
                <div class="caller-details">
                    <div class="caller-name">{{ callState.callerName || "Bilinmeyen" }}</div>
                    <div class="caller-number">{{ callState.callerNumber }}</div>
                </div>
            </div>
            <div class="call-actions">
                <button class="answer-btn" @click="handleAnswer">
                    <i class="material-icons">call</i>
                </button>
                <button class="reject-btn" @click="handleReject">
                    <i class="material-icons">call_end</i>
                </button>
            </div>
        </div>

        <!-- Audio Elements -->
        <audio
            :id="`remoteAudio-${$_.get(telnyxChannel, '_id', 'default')}`"
            ref="remoteAudio"
            autoplay
            :volume="1.0"
            :muted="false"
        ></audio>
        <audio ref="ringtone" loop>
            <source
                src="https://upload.wikimedia.org/wikipedia/commons/transcoded/3/34/Sound_Effect_-_Door_Bell.ogg/Sound_Effect_-_Door_Bell.ogg.mp3"
                type="audio/mpeg"
            />
        </audio>
        <audio ref="ringbacktone" loop>
            <source src="https://bigsoundbank.com/UPLOAD/mp3/1617.mp3" type="audio/mpeg" />
        </audio>

        <!-- Kanal Seçimi Modal -->
        <a-modal
            :visible="showChannelSelectionModal"
            title="Telefon Seçin"
            @cancel="cancelChannelSelection"
            :footer="null"
            width="400px"
        >
            <div class="channel-selection-content">
                <p class="selection-message">
                    <strong>{{ $_.get(pendingCallPayload, "phoneNumber", "Bilinmeyen") }}</strong> numarasını aramak
                    için hangi telefonu kullanmak istiyorsunuz?
                </p>

                <div class="channel-list">
                    <div
                        v-for="channel in userTelnyxChannels"
                        :key="channel._id"
                        class="channel-item"
                        @click="handleChannelSelection(channel)"
                    >
                        <div class="channel-icon">
                            <img src="/static/img/telnyx.png" alt="Telnyx" />
                        </div>
                        <div class="channel-info">
                            <div class="channel-name">{{ channel.name }}</div>
                            <div class="channel-number">{{ $_.get(channel, "payload.callerPhoneNumber", null) }}</div>
                        </div>
                        <div class="channel-arrow">
                            <i class="material-icons">chevron_right</i>
                        </div>
                    </div>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState, mapGetters } from "vuex";
import { TelnyxRTC } from "@telnyx/webrtc";
import { Howl } from "howler";
import WebPhoneDialer from "../KirmiziSantral/components/WebPhoneDialer.vue";
import WebPhoneCallHistory from "../KirmiziSantral/components/WebPhoneCallHistory.vue";
import WebPhoneContacts from "../KirmiziSantral/components/WebPhoneContacts.vue";
import log2 from "../../../../utils/log2";
import dtmfGenerator from "../../../../utils/dtmfToneGenerator";
import SYSTEM_PUBSUB from "../../../../utils/systemPubSub";

const log = log2("WebphoneTelnyx");

export default Vue.extend({
    name: "WebphoneTelnyx",
    components: {
        WebPhoneDialer,
        WebPhoneCallHistory,
        WebPhoneContacts,
    },
    props: {
        channelId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            // UI State
            isMinimized: true, // Test için başlangıçta açık
            activeTab: "dialer",
            isDragging: false,
            minimizedPosition: { bottom: 80, right: 20 },
            maximizedPosition: { bottom: 20, right: 20 },
            dragStart: { x: 0, y: 0 },
            dragThreshold: 5,

            // Telnyx WebRTC State
            telnyxClient: null,
            sipStatus: {
                isRegistered: false,
                connectionStatus: "Bağlantı bekleniyor...",
                username: "supsisphone",
                domain: "sip.telnyx.com",
            },

            // Call State
            callState: {
                status: "idle", // idle, calling, ringing, incoming, connected
                currentSession: null,
                isMuted: false,
                isOnHold: false,
                callerNumber: null,
                callerName: null,
                callStartTime: null,
                callDuration: 0,
                isIncomingCall: false, // İncoming call protection flag
            },

            // Dialer
            dialNumber: "",

            // Call History
            callHistory: [],

            // Timers
            callDurationTimer: null,
            retryTimer: null,
            directionCheckTimeouts: [], // Direction kontrol timeout'larını takip et
            extensions: [],

            // Sound System
            sounds: {
                incomingCall: new Howl({
                    src: [`./static/media/webphone/incoming-call.mp3`],
                    autoplay: false,
                    loop: true,
                    volume: 1,
                    onplayerror: (ex) => console.error("incomingCall sound error!", ex),
                }),
                ringback: new Howl({
                    src: [`./static/media/webphone/ringback.mp3`, `https://bigsoundbank.com/UPLOAD/mp3/1617.mp3`],
                    autoplay: false,
                    loop: true,
                    volume: 1,
                    onplayerror: (ex) => console.error("ringback sound error!", ex),
                }),
            },

            // Contact Overlay State (basit tutuyor şimdilik)
            isContactOverlayCollapsed: false,
            foundContacts: [],

            // Kanal Seçimi Modal
            showChannelSelectionModal: false,
            pendingCallPayload: null,

            // System PubSub
            systemPubSubSubscription: null,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            channels: (state) => state.channel.channels,
        }),
        ...mapGetters({
            isLoggedIn: "auth/isLoggedIn",
        }),
        // Kullanıcıya ait tüm Telnyx kanalları
        userTelnyxChannels() {
            return this.channels
                .filter((channel) => channel.type === "webphone-telnyx" && channel.payload?.userId === this.user._id)
                .sort((a, b) => a._id.localeCompare(b._id)); // Kanal ID'ye göre sırala
        },
        telnyxChannel() {
            if (this.channelId) {
                // Specific kanal ID'si verilmişse o kanalı bul
                return this.channels.find(
                    (channel) => channel._id === this.channelId && channel.type === "webphone-telnyx"
                );
            } else {
                // Eski davranış: kullanıcının ilk Telnyx kanalını bul
                return this.userTelnyxChannels[0];
            }
        },
        shouldShowWebPhone() {
            return (
                this.isLoggedIn &&
                this.telnyxChannel &&
                this.telnyxChannel.payload?.username &&
                this.telnyxChannel.payload?.password &&
                this.telnyxChannel.payload?.domain &&
                this.telnyxChannel.payload?.websocketServer
            );
        },
        telnyxClientConfig() {
            if (!this.telnyxChannel) return null;

            return {
                login: this.telnyxChannel.payload.username,
                password: this.telnyxChannel.payload.password,
                // Alternatif olarak JWT token kullanabilirsiniz:
                // login_token: 'your-jwt-token',
            };
        },
        telnyxConfig() {
            if (!this.telnyxChannel) return null;

            return {
                username: this.telnyxChannel.payload.username,
                password: this.telnyxChannel.payload.password,
                phoneNumber: this.telnyxChannel.payload.callerPhoneNumber,
                domain: this.telnyxChannel.payload.domain,
                websocketServer: this.telnyxChannel.payload.websocketServer,
            };
        },
        minimizedStyle() {
            if (!this.isMinimized) return {};
            return {
                bottom: `${this.minimizedPosition.bottom}px`,
                right: `${this.minimizedPosition.right}px`,
            };
        },
        maximizedWrapperStyle() {
            if (this.isMinimized) return {};
            return {
                bottom: `${this.maximizedPosition.bottom}px`,
                right: `${this.maximizedPosition.right}px`,
                width: "320px",
            };
        },
        shouldShowContactOverlay() {
            return false; // Şimdilik kapatıyorum
        },
    },
    methods: {
        ...mapActions({
            listChannels: "channel/listChannels",
        }),
        // UI Methods
        toggleMode() {
            this.isMinimized = !this.isMinimized;
            if (!this.isMinimized) {
                // WebPhone açıldığında numara girme alanına auto focus
                this.focusDialerInput();
            }
        },

        focusDialerInput() {
            this.$nextTick(() => {
                if (this.activeTab === "dialer" && this.$refs.dialerComponent) {
                    // Dialer component'inde input'a focus için method çağır
                    if (this.$refs.dialerComponent.focusInput) {
                        this.$refs.dialerComponent.focusInput();
                    }
                }
            });
        },

        startDrag(event) {
            if (!this.isMinimized) return;
            this.dragStart = { x: event.clientX, y: event.clientY };
            document.addEventListener("mousemove", this.handleDragMove);
            document.addEventListener("mouseup", this.handleDragEnd);
            event.preventDefault();
        },

        startDragMaximized(event) {
            if (this.isMinimized) return;
            if (event.target.closest(".minimize-btn")) return;
            this.dragStart = { x: event.clientX, y: event.clientY };
            document.addEventListener("mousemove", this.handleDragMove);
            document.addEventListener("mouseup", this.handleDragEnd);
            event.preventDefault();
        },

        handleDragMove(event) {
            const deltaX = Math.abs(event.clientX - this.dragStart.x);
            const deltaY = Math.abs(event.clientY - this.dragStart.y);

            if (!this.isDragging && (deltaX > this.dragThreshold || deltaY > this.dragThreshold)) {
                this.isDragging = true;
            }

            if (this.isDragging) {
                if (this.isMinimized) {
                    const newRight = Math.max(
                        10,
                        Math.min(window.innerWidth - 70, window.innerWidth - event.clientX - 30)
                    );
                    const newBottom = Math.max(
                        10,
                        Math.min(window.innerHeight - 70, window.innerHeight - event.clientY - 30)
                    );
                    this.minimizedPosition = { bottom: newBottom, right: newRight };
                } else {
                    const webphoneWidth = 320;
                    const webphoneHeight = 650;
                    const newRight = Math.max(
                        10,
                        Math.min(
                            window.innerWidth - webphoneWidth - 10,
                            window.innerWidth - event.clientX - webphoneWidth / 2
                        )
                    );
                    const newBottom = Math.max(
                        10,
                        Math.min(
                            window.innerHeight - webphoneHeight - 10,
                            window.innerHeight - event.clientY - webphoneHeight / 2
                        )
                    );
                    this.maximizedPosition = { bottom: newBottom, right: newRight };
                }
            }
        },

        handleDragEnd(event) {
            const wasDragging = this.isDragging;
            document.removeEventListener("mousemove", this.handleDragMove);
            document.removeEventListener("mouseup", this.handleDragEnd);
            if (!wasDragging) {
                this.toggleMode();
            }
            this.isDragging = false;
        },

        getMinimizedIcon() {
            switch (this.callState.status) {
                case "calling":
                    return "call";
                case "ringing":
                    return "ring_volume";
                case "incoming":
                    return "call_received";
                case "connected":
                    return "call";
                default:
                    return "phone";
            }
        },

        // Telnyx WebRTC Methods
        async initializeTelnyx() {
            if (!this.telnyxClientConfig) {
                log.error("Telnyx config not available");
                return;
            }

            try {
                // Telnyx client oluştur
                this.telnyxClient = new TelnyxRTC(this.telnyxClientConfig);

                // Audio element'i ayarla - her instance için unique ID
                this.telnyxClient.remoteElement = `remoteAudio-${this.telnyxChannel._id}`;

                // Event listener'ları ekle
                this.telnyxClient
                    .on("telnyx.ready", () => {
                        log("Telnyx client ready");
                        this.sipStatus.isRegistered = true;
                        this.sipStatus.connectionStatus = "Kayıt başarılı";
                    })
                    .on("telnyx.error", (error) => {
                        log.error("Telnyx error:", error);
                        this.sipStatus.isRegistered = false;
                        this.sipStatus.connectionStatus = "Hata: " + (error.message || error);
                        this.scheduleReconnect();
                    })
                    .on("telnyx.socket.open", () => {
                        log("Telnyx socket opened");
                        this.sipStatus.connectionStatus = "Sunucuya bağlandı";
                    })
                    .on("telnyx.socket.close", () => {
                        log("Telnyx socket closed");
                        this.sipStatus.isRegistered = false;
                        this.sipStatus.connectionStatus = "Bağlantı kesildi";
                        this.scheduleReconnect();
                    })
                    .on("telnyx.notification", (notification) => {
                        log("Telnyx notification:", notification);
                        this.handleTelnyxNotification(notification);
                    });

                // Bağlan
                await this.telnyxClient.connect();
            } catch (error) {
                log.error("Telnyx initialization error:", error);
                this.sipStatus.connectionStatus = "Başlatma hatası: " + error.message;
                this.scheduleReconnect();
            }
        },

        handleTelnyxNotification(notification) {
            log("Telnyx notification received:", notification);

            if (notification.type === "callUpdate") {
                const call = notification.call;

                log("Call update details:", {
                    state: call.state,
                    direction: call.direction,
                    remoteCallerNumber: call.remoteCallerNumber,
                    remoteNumber: call.remoteNumber,
                    currentCallStatus: this.callState.status,
                    call
                });

                switch (call.state) {
                                        case "new":
                        log("New call created", { 
                            direction: call.direction,
                            directionType: typeof call.direction,
                            callObject: call,
                            keys: Object.keys(call)
                        });
                        
                        // Direction property'si hemen mevcut olmayabilir, biraz bekleyip kontrol et
                        this.handleNewCallWithDelay(call, 0);
                        break;
                                        case "ringing":
                        log("Call ringing", { 
                            direction: call.direction, 
                            directionType: typeof call.direction,
                            directionComparison: call.direction === "inbound",
                            currentCallStatus: this.callState.status,
                            callObject: call,
                            keys: Object.keys(call),
                        });
                        
                        // Direction property'si hemen mevcut olmayabilir, biraz bekleyip kontrol et
                        this.handleCallRingingWithDelay(call, 0);
                        break;
                    case "active":
                        log("Call answered and active");
                        this.handleCallAnswered();
                        break;
                    case "hangup":
                    case "destroy":
                        log("Call ended");
                        this.handleCallHangup();
                        break;
                    case "held":
                        log("Call on hold");
                        this.callState.isOnHold = true;
                        break;
                    default:
                        log("Unknown call state:", call.state);
                        break;
                }

                // Call state'i güncelle
                const oldCurrentSession = this.callState.currentSession;
                this.callState.currentSession = call;

                log("📱 Updated currentSession:", {
                    oldSession: oldCurrentSession ? oldCurrentSession.id : null,
                    newSession: call ? call.id : null,
                    callState: this.callState.status,
                    direction: call.direction,
                });
            }
        },

        handleCallRingingWithDelay(call, retryCount = 0) {
            const maxRetries = 5;
            const delayMs = 200; // 200ms delay between retries
            
            log(`🔄 Checking call direction (attempt ${retryCount + 1}/${maxRetries})`, {
                direction: call.direction,
                directionType: typeof call.direction,
                directionExists: call.direction !== undefined && call.direction !== null,
                retryCount: retryCount,
                callId: call.id
            });
            
            // Direction property'si mevcut mu kontrol et
            const hasDirection = call.direction !== undefined && call.direction !== null && call.direction !== "";
            
            if (hasDirection) {
                // Direction mevcut, işlemi gerçekleştir
                const isIncomingCall = call.direction === "inbound" || call.direction === "incoming";
                
                if (isIncomingCall) {
                    log("✅ INCOMING CALL DETECTED - calling handleIncomingCall", {
                        direction: call.direction,
                        isIncomingCall: isIncomingCall,
                        retryCount: retryCount,
                    });
                    this.handleIncomingCall(call);
                } else {
                    // İncoming call varsa outbound call status'unu override etme
                    if (this.callState.isIncomingCall) {
                        log("🔔 INCOMING CALL ACTIVE - not overriding with outbound call status", {
                            direction: call.direction,
                            currentStatus: this.callState.status,
                            isIncomingCall: isIncomingCall,
                            callStateIsIncoming: this.callState.isIncomingCall,
                        });
                        return;
                    }
                    
                    log("📞 OUTBOUND CALL DETECTED - setting status to calling", {
                        direction: call.direction,
                        currentStatus: this.callState.status,
                        isIncomingCall: isIncomingCall,
                        retryCount: retryCount,
                    });
                    this.callState.status = "calling";
                    this.playRingbackTone();
                    // Outbound call için elapsed timer başlat
                    this.startElapsedTimer();
                }
            } else if (retryCount < maxRetries) {
                // Direction henüz mevcut değil, biraz bekleyip tekrar dene
                log(`⏳ Direction not available yet, retrying in ${delayMs}ms...`, {
                    retryCount: retryCount,
                    maxRetries: maxRetries,
                    callId: call.id
                });
                
                const timeoutId = setTimeout(() => {
                    this.removeDirectionCheckTimeout(timeoutId);
                    this.handleCallRingingWithDelay(call, retryCount + 1);
                }, delayMs);
                this.directionCheckTimeouts.push(timeoutId);
            } else {
                // Max retry'a ulaştık, default olarak incoming call olarak işle
                log("⚠️ Max retries reached, defaulting to incoming call", {
                    direction: call.direction,
                    retryCount: retryCount,
                    maxRetries: maxRetries,
                    callId: call.id
                });
                this.handleIncomingCall(call);
            }
        },

        handleNewCallWithDelay(call, retryCount = 0) {
            const maxRetries = 5;
            const delayMs = 200; // 200ms delay between retries
            
            log(`🔄 Checking NEW call direction (attempt ${retryCount + 1}/${maxRetries})`, {
                direction: call.direction,
                directionType: typeof call.direction,
                directionExists: call.direction !== undefined && call.direction !== null,
                retryCount: retryCount,
                callId: call.id
            });
            
            // Direction property'si mevcut mu kontrol et
            const hasDirection = call.direction !== undefined && call.direction !== null && call.direction !== "";
            
            if (hasDirection) {
                // Direction mevcut, kontrol et
                const isNewIncomingCall = call.direction === "inbound" || call.direction === "incoming";
                
                if (isNewIncomingCall) {
                    // İnbound call'lar için direkt incoming olarak işaretle
                    log("✅ Setting incoming call state for new inbound call", {
                        direction: call.direction,
                        isNewIncomingCall: isNewIncomingCall,
                        retryCount: retryCount,
                    });
                    this.handleIncomingCall(call);
                } else {
                    log("📞 New outbound call detected", {
                        direction: call.direction,
                        retryCount: retryCount,
                    });
                    // Outbound call için şimdilik bir şey yapmıyoruz, ringing'de handle edilecek
                }
            } else if (retryCount < maxRetries) {
                // Direction henüz mevcut değil, biraz bekleyip tekrar dene
                log(`⏳ NEW call direction not available yet, retrying in ${delayMs}ms...`, {
                    retryCount: retryCount,
                    maxRetries: maxRetries,
                    callId: call.id
                });
                
                const timeoutId = setTimeout(() => {
                    this.removeDirectionCheckTimeout(timeoutId);
                    this.handleNewCallWithDelay(call, retryCount + 1);
                }, delayMs);
                this.directionCheckTimeouts.push(timeoutId);
            } else {
                // Max retry'a ulaştık, default olarak incoming call olarak işle
                log("⚠️ NEW call max retries reached, defaulting to incoming call", {
                    direction: call.direction,
                    retryCount: retryCount,
                    maxRetries: maxRetries,
                    callId: call.id
                });
                this.handleIncomingCall(call);
            }
        },

        scheduleReconnect() {
            if (this.retryTimer) {
                clearTimeout(this.retryTimer);
            }
            this.retryTimer = setTimeout(() => {
                if (this.shouldShowWebPhone && !this.sipStatus.isRegistered) {
                    log("Attempting to reconnect...");
                    this.initializeTelnyx();
                }
            }, 5000);
        },

        // Call Methods
        handleDial(digit) {
            this.dialNumber += digit;
        },

        async handleCall(number = null) {
            const targetNumber = number || this.dialNumber;

            if (!this.telnyxClient || !this.sipStatus.isRegistered) {
                this.$message.error("Telnyx bağlantısı yok!");
                return;
            }

            if (!targetNumber.trim()) {
                this.$message.error("Lütfen bir numara girin!");
                return;
            }

            try {
                const call = this.telnyxClient.newCall({
                    destinationNumber: targetNumber,
                    callerNumber: this.telnyxConfig.phoneNumber,
                });

                this.callState = {
                    ...this.callState,
                    status: "calling",
                    currentSession: call,
                    callerNumber: targetNumber, // Bu aradığımız numara (doğru)
                    callStartTime: new Date(),
                };

                this.playRingbackTone();
                this.addToCallHistory(targetNumber, "outgoing");

                // Auto-open if minimized
                if (this.isMinimized) {
                    this.isMinimized = false;
                }
            } catch (error) {
                log.error("Call failed:", error);
                this.$message.error("Arama hatası: " + error.message);
            }
        },

        handleHangup() {
            if (this.callState.currentSession) {
                try {
                    this.callState.currentSession.hangup();
                } catch (error) {
                    log.error("hangup error:", error);
                }
            }
            setTimeout(() => {
                if (this.callState.status !== "idle") {
                    this.handleCallHangup();
                }
            }, 1000);
        },

        handleAnswer() {
            if (this.callState.currentSession && this.callState.status === "incoming") {
                try {
                    this.callState.currentSession.answer();
                } catch (error) {
                    log.error("Answer failed:", error);
                }
            }
        },

        handleReject() {
            if (this.callState.currentSession && this.callState.status === "incoming") {
                try {
                    this.callState.currentSession.hangup();
                } catch (error) {
                    log.error("Reject failed:", error);
                }
            }
            setTimeout(() => {
                if (this.callState.status !== "idle") {
                    this.handleCallHangup();
                }
            }, 1000);
        },

        handleMute() {
            if (!this.callState.currentSession) return;
            try {
                if (this.callState.isMuted) {
                    this.callState.currentSession.unmuteAudio();
                    this.callState.isMuted = false;
                } else {
                    this.callState.currentSession.muteAudio();
                    this.callState.isMuted = true;
                }
            } catch (error) {
                log.error("Mute toggle error:", error);
            }
        },

        handleHold() {
            if (!this.callState.currentSession) return;
            try {
                this.callState.currentSession.hold();
                log("Call put on hold");
            } catch (error) {
                log.error("Hold error:", error);
            }
        },

        async handleTransfer(targetExtension) {
            // Transfer functionality for Telnyx - implementation can be added later
            log("Transfer not implemented yet for Telnyx");
            this.$message.info("Transfer özelliği henüz Telnyx için eklenmedi");
        },

        handleDTMF(digit) {
            // DTMF gönder
            if (this.callState.status === "connected" && this.callState.currentSession) {
                try {
                    this.callState.currentSession.dtmf(digit);
                } catch (error) {
                    log.error("DTMF send error:", error);
                }
            }
        },

        playDTMFTone(digit = "1") {
            try {
                dtmfGenerator.playTone(digit, 150);
            } catch (error) {
                log.error("DTMF tone play error:", error);
            }
        },

        handleCallFromContacts(contact) {
            this.dialNumber = contact.number;
            this.activeTab = "dialer";
            this.handleCall(contact.number);
        },

        async handleRefreshExtensions() {
            // Telnyx için dahili listesi refresh - şimdilik empty
            this.extensions = [];
            this.$message.success("Dahili listesi yenilendi");
        },

        // Call Event Handlers
        handleIncomingCall(call) {
            log("🔔 handleIncomingCall started", {
                call: call,
                isMinimized: this.isMinimized,
                direction: call.direction,
                remoteCallerNumber: call.remoteCallerNumber,
                remoteCallerName: call.remoteCallerName,
                currentCallState: this.callState.status,
            });

            // Eğer zaten incoming durumundaysa tekrar set etme
            if (this.callState.status === "incoming" && this.callState.currentSession) {
                log("🔔 Already in incoming state, skipping duplicate incoming call setup");
                return;
            }

            // Auto-open if minimized
            if (this.isMinimized) {
                this.isMinimized = false;
                this.focusDialerInput();
            }

            // Telnyx notification'ından caller bilgilerini al
            const callerNumber = call.remoteCallerNumber || call.remoteNumber || "Bilinmeyen";
            const callerName = call.remoteCallerName || this.getContactName(callerNumber);

            log("🔔 Incoming call from:", { callerNumber, callerName });

            const oldStatus = this.callState.status;
            this.callState = {
                ...this.callState,
                status: "incoming",
                currentSession: call,
                callerNumber: callerNumber,
                callerName: callerName,
                isIncomingCall: true, // İncoming call flag'ini set et
            };

            log("🔔 Call state updated:", {
                oldStatus: oldStatus,
                newStatus: this.callState.status,
                callState: this.callState,
            });

            log("🔔 Playing ringtone...");
            this.playRingtone();
            this.addToCallHistory(callerNumber, "incoming");
        },

        handleCallAnswered() {
            this.stopAllTones();
            this.stopElapsedTimer(); // Elapsed timer'ı durdur
            this.callState = {
                ...this.callState,
                status: "connected",
                callStartTime: new Date(),
            };
            this.startCallDurationTimer();
        },

        handleCallHangup() {
            this.stopAllTones();
            this.stopElapsedTimer(); // Elapsed timer'ı durdur
            this.stopCallDurationTimer();
            this.clearDirectionCheckTimeouts(); // Direction check timeout'larını temizle
            this.callState = {
                status: "idle",
                currentSession: null,
                isMuted: false,
                isOnHold: false,
                callerNumber: null,
                callerName: null,
                callStartTime: null,
                callDuration: 0,
                isIncomingCall: false, // Flag'i reset et
            };
            this.dialNumber = "";
        },

        // Audio Methods
        playRingtone() {
            log("playRingtone called");

            try {
                // Önce Howl objesini dene
                if (this.sounds.incomingCall) {
                    log("Playing Howl incoming call sound");
                    this.sounds.incomingCall.play();
                } else {
                    log.error("Howl incomingCall object not available");
                }
            } catch (error) {
                log.error("Ringtone play error:", error);
            }
        },

        playRingbackTone() {
            log("playRingbackTone called");

            try {
                // Howl ringback sesini çal
                if (this.sounds.ringback) {
                    log("Playing Howl ringback sound");
                    this.sounds.ringback.play();
                } else {
                    log.error("Howl ringback object not available");
                    // Fallback: HTML5 audio element
                    if (this.$refs.ringbacktone) {
                        this.$refs.ringbacktone.play().catch((e) => log("Ringback play error:", e));
                    }
                }
            } catch (error) {
                log.error("Ringback tone play error:", error);
            }
        },

        stopAllTones() {
            try {
                // Howl objelerini durdur
                if (this.sounds.incomingCall) {
                    this.sounds.incomingCall.stop();
                }
                if (this.sounds.ringback) {
                    this.sounds.ringback.stop();
                }

                // HTML5 audio elementlerini durdur
                if (this.$refs.ringtone) {
                    this.$refs.ringtone.pause();
                    this.$refs.ringtone.currentTime = 0;
                }
                if (this.$refs.ringbacktone) {
                    this.$refs.ringbacktone.pause();
                    this.$refs.ringbacktone.currentTime = 0;
                }

                // DTMF generator'ı durdur
                dtmfGenerator.stop();
            } catch (error) {
                log.error("Stop tones error:", error);
            }
        },

        // Call Duration Timer
        startCallDurationTimer() {
            this.callDurationTimer = setInterval(() => {
                if (this.callState.callStartTime) {
                    this.callState.callDuration = Math.floor((new Date() - this.callState.callStartTime) / 1000);
                }
            }, 1000);
        },

        stopCallDurationTimer() {
            if (this.callDurationTimer) {
                clearInterval(this.callDurationTimer);
                this.callDurationTimer = null;
            }
        },

        // Elapsed Timer (calling/ringing durumları için)
        startElapsedTimer() {
            if (this.$refs.dialerComponent && this.$refs.dialerComponent.startElapsedTimer) {
                this.$refs.dialerComponent.startElapsedTimer();
            }
        },

        stopElapsedTimer() {
            if (this.$refs.dialerComponent && this.$refs.dialerComponent.stopElapsedTimer) {
                this.$refs.dialerComponent.stopElapsedTimer();
            }
        },

        // Call History Methods
        addToCallHistory(number, type) {
            const historyItem = {
                number: number,
                name: this.getContactName(number),
                type: type,
                date: new Date(),
                duration: this.callState.callDuration,
            };
            this.callHistory.unshift(historyItem);
            if (this.callHistory.length > 50) {
                this.callHistory = this.callHistory.slice(0, 50);
            }
            this.saveCallHistory();
        },

        loadCallHistory() {
            try {
                const saved = localStorage.getItem(`webphone_telnyx_call_history_${this.user._id}`);
                if (saved) {
                    this.callHistory = JSON.parse(saved);
                }
            } catch (error) {
                log.error("Call history load error:", error);
            }
        },

        saveCallHistory() {
            try {
                localStorage.setItem(`webphone_telnyx_call_history_${this.user._id}`, JSON.stringify(this.callHistory));
            } catch (error) {
                log.error("Call history save error:", error);
            }
        },

        // Contacts Methods
        getContactName(number) {
            // Şimdilik basit implementation - daha sonra dahili listesi eklendikçe genişletilebilir
            if (!number || number === "Bilinmeyen") return null;

            // Dahililerden isim almaya çalış (extensions listesi boş olduğu için şimdilik çalışmaz)
            const extension = this.extensions.find((ext) => ext.extension === number);
            if (extension) return extension.displayname;

            return null;
        },

        clearCallHistory() {
            this.callHistory = [];
            this.saveCallHistory();
        },

        handleCallFromHistory(call) {
            this.dialNumber = call.number;
            this.activeTab = "dialer";
            this.handleCall(call.number);
        },

        // Cleanup
        disconnect() {
            if (this.telnyxClient) {
                this.telnyxClient.disconnect();
                this.telnyxClient = null;
            }
            this.stopElapsedTimer(); // Elapsed timer'ı durdur
            this.stopCallDurationTimer();
            this.stopAllTones(); // Tüm sesleri durdur (Howl + HTML5)
            if (this.retryTimer) {
                clearTimeout(this.retryTimer);
            }
            // Direction check timeout'larını temizle
            this.clearDirectionCheckTimeouts();
        },

        // Direction Check Timeout Management
        clearDirectionCheckTimeouts() {
            this.directionCheckTimeouts.forEach(timeoutId => {
                clearTimeout(timeoutId);
            });
            this.directionCheckTimeouts = [];
        },

        removeDirectionCheckTimeout(timeoutId) {
            const index = this.directionCheckTimeouts.indexOf(timeoutId);
            if (index > -1) {
                this.directionCheckTimeouts.splice(index, 1);
            }
        },

        // External Call Request Handler
        handleExternalCallRequest(payload) {
            log("External call request received:", payload);

            // Eğer payload'da channelId varsa, sadece o kanal işlem yapsın
            if (payload.channelId) {
                if (this.telnyxChannel && this.telnyxChannel._id === payload.channelId) {
                    this.initiateCall(payload.phoneNumber);
                }
                // channelId belirtilmişse ve bu kanal değilse ignore et
                return;
            }

            // Kullanıcıya ait Telnyx kanallarını kontrol et
            const userChannels = this.userTelnyxChannels;

            if (userChannels.length === 0) {
                log("No Telnyx channels found for user");
                return;
            }

            if (userChannels.length === 1) {
                // Tek kanal varsa direkt ara
                if (this.telnyxChannel && this.telnyxChannel._id === userChannels[0]._id) {
                    this.initiateCall(payload.phoneNumber);
                }
                return;
            }

            // Birden çok kanal varsa en küçük ID'li olan modal göstersin
            const sortedChannels = [...userChannels].sort((a, b) => a._id.localeCompare(b._id));
            const firstChannel = sortedChannels[0];

            if (this.telnyxChannel && this.telnyxChannel._id === firstChannel._id) {
                // En küçük ID'li kanal modal göstersin
                this.pendingCallPayload = payload;
                this.showChannelSelectionModal = true;
            }
            // Diğer kanallar ignore etsin
        },

        initiateCall(phoneNumber) {
            if (!this.shouldShowWebPhone) {
                log.error("Webphone not available for external call");
                return;
            }

            if (this.callState.status !== "idle") {
                log.error("Cannot start external call - webphone is busy");
                return;
            }

            // Webphone'u açık moda geçir
            if (this.isMinimized) {
                this.isMinimized = false;
            }

            // Dialer tab'ına geç
            this.activeTab = "dialer";

            // Numarayı set et
            this.dialNumber = phoneNumber;

            // Input'a focus ver
            this.focusDialerInput();

            // Otomatik aramayı başlat
            setTimeout(() => {
                this.handleCall(phoneNumber);
            }, 500);

            log("External call initiated for:", phoneNumber);
        },

        handleChannelSelection(selectedChannel) {
            this.showChannelSelectionModal = false;

            if (this.pendingCallPayload && selectedChannel) {
                // Seçilen kanal ile yeni event fırlat
                const newPayload = {
                    ...this.pendingCallPayload,
                    channelId: selectedChannel._id,
                };

                // System PubSub'a yeni event gönder
                this.$nextTick(() => {
                    SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.WEBPHONE_CALL_START, newPayload);
                });
            }

            this.pendingCallPayload = null;
        },

        cancelChannelSelection() {
            this.showChannelSelectionModal = false;
            this.pendingCallPayload = null;
        },
    },
    async mounted() {
        // Call history'yi yükle
        this.loadCallHistory();

        // Kanalları yükle
        await this.listChannels();

        if (this.shouldShowWebPhone) {
            await this.initializeTelnyx();
        }

        // System PubSub listener for incoming call requests
        this.systemPubSubSubscription = SYSTEM_PUBSUB.stream.subscribe((message) => {
            if (message.type === SYSTEM_PUBSUB.MESSAGE_TYPES.WEBPHONE_CALL_START) {
                this.handleExternalCallRequest(message.payload);
            }
        });

        // Window resize handler for drag boundaries
        window.addEventListener("resize", () => {
            // Update minimized position boundaries
            this.minimizedPosition = {
                bottom: Math.max(10, Math.min(window.innerHeight - 70, this.minimizedPosition.bottom)),
                right: Math.max(10, Math.min(window.innerWidth - 70, this.minimizedPosition.right)),
            };

            // Update maximized position boundaries
            const webphoneWidth = 320;
            const webphoneHeight = 650;
            this.maximizedPosition = {
                bottom: Math.max(10, Math.min(window.innerHeight - webphoneHeight - 10, this.maximizedPosition.bottom)),
                right: Math.max(10, Math.min(window.innerWidth - webphoneWidth - 10, this.maximizedPosition.right)),
            };
        });
    },
    beforeDestroy() {
        this.disconnect();

        // System PubSub subscription'ını temizle
        if (this.systemPubSubSubscription) {
            this.systemPubSubSubscription.unsubscribe();
            this.systemPubSubSubscription = null;
        }

        // Direction check timeout'larını temizle
        this.clearDirectionCheckTimeouts();
    },
    watch: {
        shouldShowWebPhone(newVal, oldVal) {
            if (newVal && !oldVal) {
                this.initializeTelnyx();
            } else if (!newVal && oldVal) {
                this.disconnect();
            }
        },

        activeTab(newVal) {
            if (newVal === "dialer" && !this.isMinimized) {
                this.focusDialerInput();
            }
        },
    },
});
</script>

<style scoped lang="scss">
.webphone-container {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
    user-select: none;
    z-index: 9999;
}

// Minimized Mode
.webphone-minimized {
    position: fixed;
    z-index: 9999;
}

.webphone-minimized-button {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6c5ce7, #a29bfe);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    box-shadow: 0 4px 20px rgba(0, 122, 255, 0.3);
    transition: all 0.3s ease;
    position: relative;

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 6px 25px rgba(0, 122, 255, 0.4);
    }

    &:active {
        cursor: grabbing;
    }

    .material-icons {
        color: white;
        font-size: 28px;
    }

    .status-indicator {
        position: absolute;
        top: 5px;
        right: 5px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #ff3b30;
        border: 2px solid white;
    }

    &.status-connected .status-indicator {
        background: #34c759;
    }

    &.status-calling,
    &.status-ringing {
        animation: pulse 1.5s infinite;
    }

    &.incoming-call {
        animation: shake 0.5s infinite alternate;
        background: linear-gradient(135deg, #ff3b30, #d70015);
    }
}

@keyframes pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
    }
}

@keyframes shake {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(3px);
    }
}

// Maximized Mode
.webphone-maximized-wrapper {
    position: fixed;
    max-width: 620px;
    height: 650px;
    border-radius: 20px;
    overflow: hidden;
    z-index: 9999;
    display: flex;
    transition: width 0.3s ease;
    background: transparent;
}

.webphone-maximized-content {
    flex: 1;
    width: 320px;
    min-width: 320px;
    background: white;
    border-radius: 20px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.webphone-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 20px;
    background: linear-gradient(135deg, #f2f2f7, #e5e5ea);
    border-bottom: 1px solid #c6c6c8;
    cursor: grab;

    &:active {
        cursor: grabbing;
    }

    .header-center h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #1c1c1e;
    }

    .header-center .phone-number {
        font-size: 12px;
        color: #8e8e93;
        margin-top: 2px;
    }

    .sip-status {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #8e8e93;

        .status-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #ff3b30;
        }

        &.connected .status-dot {
            background: #34c759;
        }
    }

    .minimize-btn {
        background: none;
        border: none;
        padding: 5px;
        border-radius: 8px;
        cursor: pointer;

        &:hover {
            background: rgba(0, 0, 0, 0.05);
        }

        .material-icons {
            font-size: 20px;
            color: #8e8e93;
        }
    }
}

.webphone-tabs {
    display: flex;
    background: #f2f2f7;
    border-bottom: 1px solid #c6c6c8;

    .tab-btn {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        padding: 12px 8px;
        border: none;
        background: none;
        cursor: pointer;
        transition: all 0.2s ease;

        .material-icons {
            font-size: 20px;
            color: #8e8e93;
        }

        span {
            font-size: 11px;
            color: #8e8e93;
            font-weight: 500;
        }

        &.active {
            .material-icons,
            span {
                color: #007aff;
            }
        }

        &:hover {
            background: rgba(0, 0, 0, 0.05);
        }
    }
}

.webphone-content {
    flex: 1;
    overflow: hidden;
}

// Components content will be handled by WebPhoneDialer, WebPhoneCallHistory, and WebPhoneContacts

// Incoming Call Notification
.incoming-call-notification {
    position: fixed;
    top: 20px;
    right: 20px;
    width: 300px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    padding: 20px;
    z-index: 10000;
    animation: slideIn 0.3s ease;

    .caller-info {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 15px;

        .caller-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #e5e5ea;
            display: flex;
            align-items: center;
            justify-content: center;

            .material-icons {
                color: #8e8e93;
                font-size: 20px;
            }
        }

        .caller-details {
            .caller-name {
                font-weight: 600;
                color: #1c1c1e;
                margin-bottom: 2px;
            }

            .caller-number {
                font-size: 14px;
                color: #8e8e93;
            }
        }
    }

    .call-actions {
        display: flex;
        gap: 12px;

        button {
            flex: 1;
            padding: 12px;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;

            .material-icons {
                color: white;
                font-size: 20px;
            }
        }

        .answer-btn {
            background: #34c759;

            &:hover {
                background: #2fb653;
            }
        }

        .reject-btn {
            background: #ff3b30;

            &:hover {
                background: #e8322a;
            }
        }
    }
}

@keyframes slideIn {
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

.webphone-dragging {
    transition: none !important;
    cursor: grabbing !important;
}

// Dark mode support
@media (prefers-color-scheme: dark) {
    .webphone-maximized-content {
        background: #1c1c1e;
        color: white;
    }

    .webphone-header {
        background: linear-gradient(135deg, #2c2c2e, #1c1c1e);
        border-bottom-color: #3a3a3c;

        .header-center h3 {
            color: white;
        }
    }

    .webphone-tabs {
        background: #2c2c2e;
        border-bottom-color: #3a3a3c;
    }

    .incoming-call-notification {
        background: #1c1c1e;
        color: white;

        .caller-avatar {
            background: #3a3a3c;
        }
    }
}

// Kanal seçimi modal stilleri
.channel-selection-content {
    .selection-message {
        margin-bottom: 20px;
        font-size: 14px;
        color: #666;
        text-align: center;
    }

    .channel-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .channel-item {
        display: flex;
        align-items: center;
        padding: 16px;
        border: 1px solid #e8eef3;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
        background: #fff;

        &:hover {
            background: #f8f9fa;
            border-color: #007aff;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0, 122, 255, 0.15);
        }

        .channel-icon {
            width: 40px;
            height: 40px;
            margin-right: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f0f8ff;
            border-radius: 8px;

            img {
                width: 24px;
                height: 24px;
                object-fit: contain;
            }
        }

        .channel-info {
            flex: 1;

            .channel-name {
                font-weight: 600;
                color: #1c1c1e;
                margin-bottom: 4px;
            }

            .channel-number {
                font-size: 13px;
                color: #8e8e93;
            }
        }

        .channel-arrow {
            color: #8e8e93;

            .material-icons {
                font-size: 20px;
            }
        }
    }
}

@media (prefers-color-scheme: dark) {
    .channel-selection-content {
        .selection-message {
            color: #ccc;
        }

        .channel-item {
            background: #2c2c2e;
            border-color: #3a3a3c;

            &:hover {
                background: #3a3a3c;
                border-color: #007aff;
            }

            .channel-icon {
                background: #3a3a3c;
            }

            .channel-info .channel-name {
                color: white;
            }
        }
    }
}
</style>
